/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ExclamationTriangleIcon, ArrowRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { kycService } from '../../services/api';
import { KYC, KYCStatus, DiditStatus } from '../../types';
import { isKYCEnforcementEnabled } from '../../config/featureFlags';
import { useAuthStore } from '../../store/authStore';

const KYCBanner: React.FC = () => {
  const location = useLocation();
  const [dismissed, setDismissed] = React.useState(false);
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();

  // Don't show banner if KYC enforcement is disabled
  const kycEnforcementEnabled = isKYCEnforcementEnabled();

  // Don't show banner on the KYC page itself
  const isKYCPage = location.pathname === '/kyc' || location.pathname === '/kyc/callback';

  const { data: kycData, isLoading, isPending: kycPending } = useQuery({
    queryKey: ['kyc-status-banner'],
    queryFn: async () => {
      const response = await kycService.getStatus();
      return response.data as KYC | null;
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    retry: false,
    refetchOnWindowFocus: false,
    enabled: kycEnforcementEnabled && isAuthenticated && !authLoading,
  });

  // Don't render if KYC enforcement disabled, on KYC page, loading, or dismissed
  if (!kycEnforcementEnabled || isKYCPage || isLoading || kycPending || dismissed) {
    return null;
  }

  // Determine if KYC is pending or needs action
  const isPending = isKYCPending(kycData);

  if (!isPending) {
    return null;
  }

  const message = getKYCMessage(kycData);

  return (
    <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 border-b border-amber-500/20">
      <div className="px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="flex-shrink-0 p-1.5 bg-amber-500/20 rounded-lg">
              <ExclamationTriangleIcon className="w-4 h-4 text-amber-400" />
            </div>
            <p className="text-sm text-amber-200 truncate">
              <span className="font-medium">KYC Verification Required:</span>{' '}
              <span className="text-amber-300/80">{message}</span>
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Link
              to="/kyc"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-black bg-amber-400 hover:bg-amber-300 rounded-lg transition-colors"
            >
              Complete KYC
              <ArrowRightIcon className="w-3.5 h-3.5" />
            </Link>
            <button
              onClick={() => setDismissed(true)}
              className="p-1.5 text-amber-400/60 hover:text-amber-400 hover:bg-amber-500/10 rounded-lg transition-colors"
              aria-label="Dismiss banner"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Check if KYC needs user attention (pending, not submitted, in progress, etc.)
 */
function isKYCPending(kyc: KYC | null | undefined): boolean {
  if (!kyc) {
    // No KYC record means not submitted
    return true;
  }

  const status = kyc.status as KYCStatus;
  const diditStatus = kyc.didit_status as DiditStatus | undefined;

  // If Didit verification is approved, KYC is complete
  if (diditStatus === 'Approved') {
    return false;
  }

  // If QuickeKYC is verified, KYC is complete
  if (status === 'verified') {
    return false;
  }

  // All other statuses require attention
  return true;
}

/**
 * Get appropriate message based on KYC status
 */
function getKYCMessage(kyc: KYC | null | undefined): string {
  if (!kyc) {
    return 'You can explore the platform but cannot provision any services until KYC is completed.';
  }

  const status = kyc.status as KYCStatus;
  const diditStatus = kyc.didit_status as DiditStatus | undefined;

  // Check Didit status first
  if (diditStatus) {
    switch (diditStatus) {
      case 'In Progress':
        return 'Your verification is in progress. Please complete the verification process.';
      case 'In Review':
        return 'Your verification is being reviewed. This usually takes a few minutes.';
      case 'Declined':
        return 'Your verification was declined. Please try again with valid documents.';
      case 'Abandoned':
        return 'Your verification session was abandoned. Please start a new verification.';
      case 'Expired':
        return 'Your verification session has expired. Please start a new verification.';
      case 'Not Started':
        return 'You can explore the platform but cannot provision any services until KYC is completed.';
    }
  }

  // Fall back to QuickeKYC status
  switch (status) {
    case 'pending':
      return 'Your KYC is under review. You can explore but cannot provision services.';
    case 'in_progress':
      return 'Your verification is in progress. Please complete the process.';
    case 'rejected':
      return kyc.rejection_reason || kyc.reject_reason || 'Your KYC was rejected. Please resubmit with valid details.';
    case 'expired':
      return 'Your KYC has expired. Please submit a new verification.';
    case 'not_submitted':
    default:
      return 'You can explore the platform but cannot provision any services until KYC is completed.';
  }
}

export default KYCBanner;
